import { useEffect, useMemo, useState } from "react";
import { useHousehold } from "../../app/useHousehold";
import { EmptyState } from "../../ui/StatusBadge";
import type { WidgetDefinition, WidgetProps } from "../types";
import { ArticleLink } from "./ArticleSheet";
import { interleave, NewsImage, SourceBadge, storyAge, useNewsSources } from "./newsUi";

type HeroConfig = Record<string, never>;

const PER_SOURCE = 5;

/**
 * The News screen's big story: top stories with pictures from the sources
 * picked for it (Admin → News & sports), taking turns. Tap to read the story.
 */
function NewsHero({ editing }: WidgetProps<HeroConfig>) {
  const { settings, isAdmin } = useHousehold();
  const news = settings.news;
  const ids = news.hero.length ? news.hero : news.sources.slice(0, 2);
  const qs = useNewsSources(ids);
  const lists = qs.map((q) => q.data?.items ?? []);
  const key = lists.map((l) => l.map((i) => i.id).join()).join("|");
  const stories = useMemo(() => {
    const pictured = lists.map((l) => l.filter((i) => i.image).slice(0, PER_SOURCE));
    const pool = pictured.some((l) => l.length) ? pictured : lists.map((l) => l.slice(0, PER_SOURCE));
    return interleave(pool, PER_SOURCE * ids.length);
    // Recomputed only when the stories themselves change, not on every refetch.
  }, [key]);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (editing || stories.length < 2) return;
    const t = window.setTimeout(() => setI((n) => (n + 1) % stories.length), news.heroSeconds * 1000);
    return () => clearTimeout(t);
  }, [i, stories.length, editing, news.heroSeconds]);

  if (!ids.length)
    return (
      <EmptyState
        icon="news"
        title="Pick your news"
        detail="Choose the sources for the News screen and its top story."
        action={
          isAdmin ? (
            <a className="btn btn-sm btn-primary" href="/admin#news">
              Pick sources
            </a>
          ) : undefined
        }
      />
    );
  const s = stories[i % Math.max(1, stories.length)];
  if (!s) return qs.some((q) => q.isLoading) ? <div className="widget-loading" /> : <EmptyState icon="news" title="No stories right now" detail={qs.find((q) => q.error)?.error?.message} />;

  const body = (
    <>
      <NewsImage key={s.id} src={s.image} className="news-hero-img" />
      <span className="news-hero-shade" aria-hidden="true" />
      <span className="news-hero-body">
        <span className="news-hero-meta">
          <SourceBadge id={s.source} />
          {storyAge(s) && <span>{storyAge(s)}</span>}
        </span>
        <span className="news-hero-title">{s.title}</span>
        {s.summary && <span className="news-hero-summary">{s.summary}</span>}
      </span>
      {stories.length > 1 && (
        <span className="news-hero-dots" aria-hidden="true">
          {stories.map((x, n) => (
            <i key={x.id} className={n === i % stories.length ? "on" : ""} />
          ))}
        </span>
      )}
    </>
  );
  return (
    <ArticleLink story={s} className={`news-hero ${s.image ? "" : "no-image"}`} ariaLabel={`${s.title}. Read the story.`}>
      {body}
    </ArticleLink>
  );
}

export const newsHeroWidget: WidgetDefinition<HeroConfig> = {
  type: "news-hero",
  version: 1,
  name: "Top story",
  description: "Big rotating top stories with pictures from the sources you pick for it.",
  icon: "news",
  category: "household",
  defaultSize: { w: 8, h: 6 },
  minSize: { w: 4, h: 3 },
  stackHeight: 380,
  defaults: {},
  settings: [],
  provider: "rss",
  Component: NewsHero,
};
